import clsx from 'clsx';

export type ProgressBarTone = 'brand' | 'success' | 'warning' | 'danger';
export type ProgressBarSize = 'sm' | 'md' | 'lg';

export type ProgressBarProps = {
  /** Percentage 0-100. Clamped if outside the range. */
  value: number;
  tone?: ProgressBarTone;
  size?: ProgressBarSize;
  /** Optional aria-label; defaults to a generic "progress: N%". */
  'aria-label'?: string;
};

const trackSizes: Record<ProgressBarSize, string> = {
  sm: 'h-1',
  md: 'h-[6px]',
  lg: 'h-2',
};

const fillTones: Record<ProgressBarTone, string> = {
  brand: 'bg-brand-primary',
  success: 'bg-success-strong',
  warning: 'bg-warning-fg',
  danger: 'bg-danger-fg',
};

export function ProgressBar({
  value,
  tone = 'brand',
  size = 'md',
  'aria-label': ariaLabel,
}: ProgressBarProps) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <span
      data-tone={tone}
      data-size={size}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-label={ariaLabel ?? `progress: ${Math.round(pct)}%`}
      className={clsx(
        'block w-full overflow-hidden rounded-pill bg-surface-workspace',
        trackSizes[size],
      )}
    >
      <span
        className={clsx(
          'block h-full rounded-pill transition-[width] duration-medium ease-standard',
          fillTones[tone],
        )}
        style={{ width: `${pct}%` }}
      />
    </span>
  );
}
